// Cloudflare TURN credentials, shared by every call window.
//
// Credentials are fetched from the Pages Function at /turn-credentials with the signed-in
// user's Firebase ID token, cached until shortly before they expire, and prefetched as soon
// as the window opens so call setup never waits on this request.

const ICE_CANDIDATE_POOL_SIZE = 10;
const STUN_SERVERS = [
  {
    urls: [
      "stun:stun1.l.google.com:19302",
      "stun:stun2.l.google.com:19302",
      "stun:stun.cloudflare.com:3478"
    ]
  }
];
const TURN_REFRESH_MARGIN_MS = 5 * 60 * 1000; // refresh ~5 min before expiry

let turnCache = null;      // { iceServers, expiresAt }
let inflight = null;       // Promise while a fetch is running
let refreshTimer = null;

async function fetchTurnCredentials(user) {
  if (!user || typeof user.getIdToken !== "function") {
    throw new Error("no signed-in user");
  }
  const idToken = await user.getIdToken();
  const response = await fetch("/turn-credentials", {
    method: "POST",
    headers: { Authorization: `Bearer ${idToken}` }
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = await response.json();
  // Cloudflare returns iceServers as an object or an array depending on API version.
  let iceServers = data.iceServers;
  if (iceServers && !Array.isArray(iceServers)) {
    iceServers = [iceServers];
  }
  if (!Array.isArray(iceServers) || iceServers.length === 0) {
    throw new Error("response contained no iceServers");
  }
  const ttlSeconds = Number(data.ttl) > 0 ? Number(data.ttl) : 86400;
  const expiresAt =
    Number(data.expiresAt) > 0 ? Number(data.expiresAt) : Date.now() + ttlSeconds * 1000;
  return { iceServers, expiresAt };
}

function isFresh() {
  return turnCache && Date.now() < turnCache.expiresAt - TURN_REFRESH_MARGIN_MS;
}

/** Fetch (or reuse) credentials. Concurrent callers share one request. */
async function refresh(user) {
  if (isFresh()) return turnCache;
  if (!inflight) {
    inflight = fetchTurnCredentials(user)
      .then(result => {
        turnCache = result;
        console.log("TURN credentials refreshed; valid until", new Date(result.expiresAt).toISOString());
        scheduleRefresh(user);
        return result;
      })
      .catch(err => {
        turnCache = null;
        console.warn("TURN credentials unavailable, falling back to STUN only:", err && err.message ? err.message : err);
        return null;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

function scheduleRefresh(user) {
  clearTimeout(refreshTimer);
  if (!turnCache) return;
  const delay = Math.max(60 * 1000, turnCache.expiresAt - TURN_REFRESH_MARGIN_MS - Date.now());
  refreshTimer = setTimeout(() => {
    turnCache = null;
    refresh(user);
  }, delay);
}

/** Call when the window opens so the first call does not wait on the network. */
export function prefetchTurnCredentials(user) {
  refresh(user);
}

/**
 * RTCPeerConnection configuration; waits for an in-flight prefetch if there is one.
 * options.forceRelay: use only TURN relay candidates (testing aid). Ignored, with a
 * warning, when no TURN credentials are available since a relay-only call would
 * then have no route at all.
 */
export async function getRtcConfiguration(user, options = {}) {
  await refresh(user);
  const config = {
    iceServers: turnCache ? turnCache.iceServers : STUN_SERVERS,
    iceCandidatePoolSize: ICE_CANDIDATE_POOL_SIZE
  };
  if (options.forceRelay) {
    if (turnCache) {
      config.iceTransportPolicy = "relay";
      console.log("Force relay is on: direct routes ignored, call goes through TURN");
    } else {
      console.warn("Force relay requested but no TURN credentials; allowing any route");
    }
  }
  return config;
}
